import { isMatchUpcoming } from '@/lib/matchTime';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { matchesAPI, leaguesAPI, facilitiesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Calendar, Trophy, Users, MapPin, ChevronRight,
  Plus, Clock, Building2, ArrowRight,
  Search, Megaphone,
} from 'lucide-react';
import {
  formatDate, formatTime, getRelativeTime,
  getStatusColorClass, getStatusLabel, getInitials,
} from '@/lib/utils';

// =============================================================================
// Helpers
// =============================================================================

const formatRelativeShort = (iso) => {
  if (!iso) return '';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'ora';
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `Tra ${days}g ${hours % 24}h`;
  if (hours >= 1) return `Tra ${hours}h ${minutes % 60}m`;
  return `Tra ${minutes}m`;
};

// Live countdown chip — ticks every minute
const Countdown = ({ dateTime }) => {
  const [, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  return formatRelativeShort(dateTime);
};

// =============================================================================

export const Dashboard = () => {
  const { user, facilityData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allMatches, setAllMatches] = useState([]);
  const [myLeagues, setMyLeagues] = useState([]);
  const [myFacilities, setMyFacilities] = useState([]);

  // Facility owners go straight to their facility dashboard
  useEffect(() => {
    if (user?.account_type === 'facility' && facilityData?.facility_id) {
      navigate(`/facilities/${facilityData.facility_id}/dashboard`, { replace: true });
    }
  }, [user, facilityData, navigate]);

  useEffect(() => {
    if (user?.account_type === 'facility') return;
    const fetchData = async () => {
      try {
        const [matchesRes, leaguesRes, facRes] = await Promise.all([
          matchesAPI.getMyMatches(),
          leaguesAPI.getMyLeagues(),
          facilitiesAPI.getMy().catch(() => ({ data: [] })),
        ]);

        setAllMatches(matchesRes.data || []);
        setMyLeagues(leaguesRes.data || []);
        setMyFacilities(facRes.data || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // ─── Derived ───────────────────────────────────────────────────────────────
  const upcoming = allMatches
    .filter((m) => isMatchUpcoming(m))
    .sort((a, b) => new Date(a.date_time) - new Date(b.date_time));

  const next = upcoming[0];

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'Giocatore';

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="dashboard">
      {/* ── HERO welcome ─────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 text-white p-5 md:p-7">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%)',
        }} />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14 md:h-16 md:w-16 ring-4 ring-white/30">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="text-xl bg-white/20 text-white">{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Ciao, {firstName}!</h1>
              <p className="text-white/85 text-sm md:text-base mt-0.5">
                {next
                  ? `La tua prossima partita è ${formatRelativeShort(next.date_time).toLowerCase()}`
                  : 'Pronto per la prossima partita?'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/matches/new">
              <Button className="bg-white text-indigo-700 hover:bg-white/95 font-bold shadow" data-testid="create-match-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuova partita
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* ── Featured "Prossima partita" ──────────────────────────────────── */}
      {next && (
        <Link to={`/matches/${next.match_id}`} className="block">
          <Card className="overflow-hidden border-2 border-emerald-200 dark:border-emerald-900 hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-emerald-500 to-blue-500 text-white px-4 py-2 flex items-center justify-between text-sm font-medium">
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                <Countdown dateTime={next.date_time} />
              </span>
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                {formatDate(next.date_time, { weekday: 'short', day: 'numeric', month: 'short' })} · {formatTime(next.date_time)}
              </span>
            </div>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="secondary" className="text-xs">{next.sport_name}</Badge>
                    {next.league_name && (
                      <span className="text-xs text-muted-foreground truncate">{next.league_name}</span>
                    )}
                  </div>
                  <h3 className="font-bold text-lg leading-tight">{next.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{next.location_name}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" />
                    {next.participants_count || 0}/{next.max_participants || 10} partecipanti
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* ── Layout 2-col ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* MAIN — Upcoming list (excluding the featured one) */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{next ? 'Altre partite' : 'Prossime partite'}</h2>
            <Link to="/matches">
              <Button variant="ghost" size="sm">
                Vedi tutte <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>

          {upcoming.length <= 1 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-4 text-sm">
                  {upcoming.length === 0
                    ? 'Non hai partite in programma'
                    : 'Solo una partita in programma'}
                </p>
                <div className="flex justify-center gap-2">
                  <Link to="/search">
                    <Button variant="outline" size="sm">
                      <Search className="h-4 w-4 mr-2" />
                      Cerca partite
                    </Button>
                  </Link>
                  <Link to="/matches/new">
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Crea partita
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {upcoming.slice(1, 6).map((match) => (
                <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                  <Card className="card-interactive hover:shadow-sm transition">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="secondary" className="text-xs">{match.sport_name}</Badge>
                            <Badge className={`${getStatusColorClass(match.status)} text-xs`}>
                              {getStatusLabel(match.status)}
                            </Badge>
                          </div>
                          <h3 className="font-semibold truncate">{match.title}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">{match.location_name}</span>
                          </p>
                        </div>
                        <div className="text-right shrink-0 text-sm">
                          <p className="font-medium">{formatRelativeShort(match.date_time)}</p>
                          <p className="text-xs text-muted-foreground">{formatTime(match.date_time)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}


        </div>

        {/* SIDEBAR */}
        <div className="space-y-6">
          {/* My Leagues — compact */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Le mie leghe</CardTitle>
              <Link to="/leagues">
                <Button variant="ghost" size="sm" className="h-7"><ChevronRight className="h-4 w-4" /></Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-1">
              {myLeagues.length === 0 ? (
                <div className="text-center py-3">
                  <p className="text-sm text-muted-foreground mb-2">Non sei in nessuna lega</p>
                  <Link to="/leagues">
                    <Button size="sm" variant="outline">Esplora leghe</Button>
                  </Link>
                </div>
              ) : (
                myLeagues.slice(0, 4).map((league) => (
                  <Link
                    key={league.league_id}
                    to={`/leagues/${league.league_id}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shrink-0">
                      <Trophy className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{league.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{league.sport_name} · {league.city}</p>
                    </div>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>

          {/* My Facilities */}
          {myFacilities.length > 0 && (
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Le mie strutture</CardTitle>
                <Link to="/facilities">
                  <Button variant="ghost" size="sm" className="h-7"><ChevronRight className="h-4 w-4" /></Button>
                </Link>
              </CardHeader>
              <CardContent className="space-y-1">
                {myFacilities.slice(0, 3).map((fac) => (
                  <Link
                    key={fac.facility_id}
                    to={`/facilities/${fac.facility_id}/dashboard`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 text-white flex items-center justify-center shrink-0">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{fac.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{fac.city} · {fac.fields_count || 0} campi</p>
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}


        </div>
      </div>
    </div>
  );
};

export default Dashboard;
